import FormActionButtons from '@components/FormActionButtons';
import { Autocomplete, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { searchArea, fetchLatLongFromCityPostalCode } from '@components/InputCity/hereApi';
import { City } from '@components/InputCity/types';

type GeoLocationValues = {
    lat: number;
    lng: number;
    address?: string;
    city?: string;
    postal?: string;
};

type AddressSuggestion = {
    id: string;
    title: string;
    address: { label: string };
    position?: { lat: number; lng: number };
};

type CompanyGeoLocationFormProps = {
    initialLat?: number;
    initialLng?: number;
    initialAddress?: string;
    initialCity?: string;
    initialPostal?: string;
    loading?: boolean;
    onCancel: () => void;
    onSubmit: (values: GeoLocationValues) => Promise<void> | void;
};

const DEFAULT_LAT = 48.8566;
const DEFAULT_LNG = 2.3522;
const HERE_API_KEY = process.env.REACT_APP_PUBLIC_HERE_API_KEY;

const CompanyGeoLocationForm = ({
    initialLat,
    initialLng,
    initialAddress,
    initialCity,
    initialPostal,
    loading = false,
    onCancel,
    onSubmit,
}: CompanyGeoLocationFormProps) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markerRef = useRef<any>(null);

    const [lat, setLat] = useState<number>(initialLat ?? DEFAULT_LAT);
    const [lng, setLng] = useState<number>(initialLng ?? DEFAULT_LNG);
    const [city, setCity] = useState<string>(initialCity ?? '');
    const [postal, setPostal] = useState<string>(initialPostal ?? '');
    const [isDirty, setIsDirty] = useState(false);

    // Track current map center for search context
    const [mapCenter, setMapCenter] = useState({ lat: initialLat ?? DEFAULT_LAT, lng: initialLng ?? DEFAULT_LNG });
    const [mapBounds, setMapBounds] = useState<string>('');

    // City/Postal selector for initial location
    const [cityPostalOptions, setCityPostalOptions] = useState<City[]>([]);
    const [cityPostalLoading, setCityPostalLoading] = useState(false);
    const [cityPostalInput, setCityPostalInput] = useState('');
    const [selectedCityPostal, setSelectedCityPostal] = useState<City | null>(null);

    // Address autocomplete
    const [addressInput, setAddressInput] = useState(initialAddress ?? '');
    const [selectedAddress, setSelectedAddress] = useState(initialAddress ?? '');
    const [addressOptions, setAddressOptions] = useState<AddressSuggestion[]>([]);
    const [addressLoading, setAddressLoading] = useState(false);

    const updateMarkerPosition = useCallback((newLat: number, newLng: number) => {
        if (!mapInstanceRef.current || !markerRef.current) return;
        markerRef.current.setGeometry({ lat: newLat, lng: newLng });
        mapInstanceRef.current.setCenter({ lat: newLat, lng: newLng });
    }, []);

    // City/Postal search
    useEffect(() => {
        if (!cityPostalInput || cityPostalInput.length < 2) {
            setCityPostalOptions([]);
            return;
        }
        const timer = setTimeout(() => {
            setCityPostalLoading(true);
            searchArea(cityPostalInput, (results) => {
                setCityPostalOptions(results ?? []);
                setCityPostalLoading(false);
            });
        }, 350);
        return () => clearTimeout(timer);
    }, [cityPostalInput]);

    // Handle city/postal selection - center map on selected location
    const handleCityPostalSelect = async (option: City | string | null) => {
        if (!option || typeof option === 'string') {
            setSelectedCityPostal(null);
            return;
        }

        setSelectedCityPostal(option);
        setCity(option.address.city);
        setPostal(option.address.postalCode);
        setIsDirty(true);

        // Fetch lat/lng for the selected city/postal and center map
        try {
            const data = await fetchLatLongFromCityPostalCode(
                option.address.city,
                option.address.postalCode
            );
            
            const position = data?.items?.[0]?.position ?? data?.position;
            if (position) {
                const newLat = parseFloat(position.lat.toFixed(6));
                const newLng = parseFloat(position.lng.toFixed(6));
                
                // Update coordinates state
                setLat(newLat);
                setLng(newLng);
                
                // Update marker and center map using helper function
                updateMarkerPosition(newLat, newLng);
                
                // Set zoom level
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.setZoom(14);
                }
            }
        } catch (error) {
            console.error('Failed to fetch location for city/postal:', error);
        }
    };

    // Debounced address autocomplete via HERE Autosuggest
    // Uses current map center for context-aware search
    useEffect(() => {
        if (!addressInput || addressInput.length < 3 || !HERE_API_KEY) {
            setAddressOptions([]);
            return;
        }
        const timer = setTimeout(async () => {
            setAddressLoading(true);
            try {
                // Build URL with map-based context
                // Note: 'at' and 'in=bbox' are mutually exclusive, prefer bbox for area constraint
                let url = `https://autosuggest.search.hereapi.com/v1/autosuggest?q=${encodeURIComponent(addressInput)}&apikey=${HERE_API_KEY}&lang=fr&limit=6&in=countryCode:FRA`;
                
                if (mapBounds) {
                    // Use bounding box to constrain results to visible map area
                    url += `&in=${mapBounds}`;
                } else {
                    // Fallback to center point bias if bbox not available yet
                    url += `&at=${mapCenter.lat},${mapCenter.lng}`;
                }
                
                const res = await fetch(url);
                const data = await res.json();
                const items: AddressSuggestion[] = (data.items ?? []).filter(
                    (item: any) => item.resultType !== 'categoryQuery' && item.resultType !== 'chainQuery'
                );
                setAddressOptions(items);
            } catch {
                setAddressOptions([]);
            } finally {
                setAddressLoading(false);
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [addressInput, mapCenter, mapBounds]);

    const handleAddressSelect = async (option: AddressSuggestion | string | null) => {
        if (!option || typeof option === 'string') {
            setSelectedAddress(typeof option === 'string' ? option : '');
            return;
        }

        setSelectedAddress(option.address?.label ?? option.title);

        let position = option.position;
        let addressDetails: any = null;

        // Fallback: lookup precise position by id
        if (!position && option.id && HERE_API_KEY) {
            try {
                const res = await fetch(
                    `https://lookup.search.hereapi.com/v1/lookup?id=${encodeURIComponent(option.id)}&apikey=${HERE_API_KEY}`
                );
                const data = await res.json();
                position = data.position;
                addressDetails = data.address;
            } catch { /* use manual entry */ }
        }

        if (position) {
            const newLat = parseFloat(position.lat.toFixed(6));
            const newLng = parseFloat(position.lng.toFixed(6));
            setLat(newLat);
            setLng(newLng);
            setIsDirty(true);
            updateMarkerPosition(newLat, newLng);
        }

        // Extract city and postal code from address details
        if (addressDetails) {
            if (addressDetails.postalCode) {
                setPostal(addressDetails.postalCode);
            }
            if (addressDetails.city) {
                setCity(addressDetails.city);
            }
        }
    };

    useEffect(() => {
        if (!mapRef.current || !HERE_API_KEY) return;

        let H: any;
        try {
            const mod = require('@here/maps-api-for-javascript');
            H = mod.default || mod;
        } catch {
            return;
        }

        let map: any;
        let resizeObserver: ResizeObserver;
        const container = mapRef.current;

        const rafId = requestAnimationFrame(() => {
            if (!container) return;

            const platform = new H.service.Platform({ apikey: HERE_API_KEY });
            const defaultLayers = platform.createDefaultLayers();

            const mapLayer = defaultLayers?.vector?.normal?.map ?? defaultLayers?.raster?.normal?.map;
            if (!mapLayer) return;

            map = new H.Map(container, mapLayer, {
                zoom: 13,
                center: { lat, lng },
            });

            const mapEvents = new H.mapevents.MapEvents(map);
            new H.mapevents.Behavior(mapEvents);
            H.ui.UI.createDefault(map, defaultLayers);

            const marker = new H.map.Marker({ lat, lng }, { volatility: true });
            marker.draggable = true;
            map.addObject(marker);
            markerRef.current = marker;
            mapInstanceRef.current = map;

            map.addEventListener('tap', (evt: any) => {
                const coord = map.screenToGeo(
                    evt.currentPointer.viewportX,
                    evt.currentPointer.viewportY,
                );
                const newLat = parseFloat(coord.lat.toFixed(6));
                const newLng = parseFloat(coord.lng.toFixed(6));
                marker.setGeometry({ lat: newLat, lng: newLng });
                setLat(newLat);
                setLng(newLng);
                setIsDirty(true);
            });

            map.addEventListener('dragstart', (evt: any) => {
                if (evt.target instanceof H.map.Marker) {
                    map.getViewPort().element.style.cursor = 'grabbing';
                }
            }, false);

            map.addEventListener('dragend', (evt: any) => {
                if (evt.target instanceof H.map.Marker) {
                    map.getViewPort().element.style.cursor = 'auto';
                    const position = evt.target.getGeometry();
                    const newLat = parseFloat(position.lat.toFixed(6));
                    const newLng = parseFloat(position.lng.toFixed(6));
                    setLat(newLat);
                    setLng(newLng);
                    setIsDirty(true);
                }
            }, false);

            map.addEventListener('drag', (evt: any) => {
                if (evt.target instanceof H.map.Marker) {
                    const coord = map.screenToGeo(
                        evt.currentPointer.viewportX,
                        evt.currentPointer.viewportY,
                    );
                    evt.target.setGeometry(coord);
                }
            }, false);

            // Track map center changes for context-aware address search
            map.addEventListener('mapviewchangeend', () => {
                const center = map.getCenter();
                const bounds = map.getViewModel().getLookAtData().bounds.getBoundingBox();
                setMapCenter({ lat: center.lat, lng: center.lng });
                // Format bounds as "bbox:west,south,east,north" for HERE API
                setMapBounds(`bbox:${bounds.getLeft()},${bounds.getBottom()},${bounds.getRight()},${bounds.getTop()}`);
            });

            resizeObserver = new ResizeObserver(() => map.getViewPort().resize());
            resizeObserver.observe(container);
        });

        return () => {
            cancelAnimationFrame(rafId);
            resizeObserver?.disconnect();
            map?.dispose();
            mapInstanceRef.current = null;
            markerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit({ 
            lat, 
            lng, 
            address: selectedAddress || undefined,
            city: city || undefined,
            postal: postal || undefined,
        });
    };

    return (
        <Stack component="form" onSubmit={handleSubmit} spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="secondary" sx={{ fontWeight: 500 }}>
                <i className="fi fi-br-marker" style={{ marginRight: 6 }}></i>
                 Sélectionnez d'abord votre ville ou code postal
            </Typography>
            
            <Autocomplete<City, false, false, true>
                freeSolo
                options={cityPostalOptions}
                loading={cityPostalLoading}
                inputValue={cityPostalInput}
                value={selectedCityPostal}
                getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.label
                }
                filterOptions={(x) => x}
                onInputChange={(_, value) => setCityPostalInput(value)}
                onChange={(_, value) => handleCityPostalSelect(value)}
                renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.locationId}>
                        <Typography variant="body2" fontSize="13px">
                            {option.label}
                        </Typography>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Ville ou code postal"
                        size="small"
                        placeholder="Ex: Lyon, 69001, Paris..."
                        disabled={loading}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {cityPostalLoading && <CircularProgress size={14} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                La carte se centre automatiquement sur votre sélection. Cliquez ou faites glisser le marqueur pour affiner la position.
            </Typography>

            <Box
                ref={mapRef}
                sx={{
                    width: '100%',
                    height: 300,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            />

            <Box sx={{ pt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Ou recherchez une adresse précise dans la zone visible :
                </Typography>
                
                <Autocomplete<AddressSuggestion, false, false, true>
                    freeSolo
                    options={addressOptions}
                    loading={addressLoading}
                    inputValue={addressInput}
                    getOptionLabel={(option) =>
                        typeof option === 'string' ? option : (option.address?.label ?? option.title)
                    }
                    filterOptions={(x) => x}
                    onInputChange={(_, value) => setAddressInput(value)}
                    onChange={(_, value) => handleAddressSelect(value)}
                    renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" fontSize="13px">
                                    {option.title}
                                </Typography>
                                {option.address?.label !== option.title && (
                                    <Typography variant="caption" color="text.secondary" fontSize="11px">
                                        {option.address?.label}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Rechercher une adresse"
                            size="small"
                            placeholder="Ex: 12 rue de Rivoli"
                            disabled={loading}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {addressLoading && <CircularProgress size={14} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Box>

            <FormActionButtons
                onCancel={onCancel}
                loading={loading}
                submitDisabled={!isDirty || loading}
                containerSx={{ pt: 0.5 }}
                size="small"
            />
        </Stack>
    );
};

export default CompanyGeoLocationForm;
